import { Alert, Badge, Card, Table } from "flowbite-react";
import {
  formatToReal,
  getImageURLScore,
  getScoreDescricao,
} from "../../utils/utils";
import moment from "moment";
import GraficoConsultasHistorico from "./GraficoConsultasHistorico";
import {
  FaBalanceScale,
  FaCalendarAlt,
  FaRegCalendarAlt,
} from "react-icons/fa";
import { FaChartLine, FaHandshake, FaRegCreditCard } from "react-icons/fa6";
import ReactApexChart from "react-apexcharts";
import TitleStripe from "../common/TitleStripe";
import NoOcurrence from "../common/query/NoOcurrence";

const debtTypeLabels = {
  BANKRUPTSPATICIPATION: "Participação em Falência",
  CHECKCCF: "Cheques Sem Fundos",
  COLLECTIONRECORDS: "Cobranças",
  FINANCIAL: "Financeiro",
  JUDGEMENTFILINGS: "Ações Judiciais",
  MARKET: "Comercial",
  NOTARY: "Protestos em Cartório",
};

const scoreParamLabels = {
  valorTotalDividasVencidas: "Valor total de dívidas vencidas",
  valorMedioCompra: "Valor médio de compra",
  valorTotalCompras: "Valor total de compras",
  valorUltimaCompra: "Valor da última compra",
  valorMaximoCompra: "Maior valor de compra",
  valorMedioPagamentos: "Valor médio de pagamentos",
  valorTotalPagamentos: "Valor total de pagamentos",
  valorEmMilhares: "Valor em milhares",
  valorHistoricoCredito: "Histórico de crédito",
  pontuacaoDe: "Pontuação de",
  pontuacaoAte: "Pontuação até",
};
const formatThousands = (value) => {
  const val = parseInt(value, 10);
  if (isNaN(val)) return value;
  return val.toLocaleString("pt-BR") + " mil";
};

const RelatorioAvancadoPJQuery = ({ data }) => {
  const optionalQsa = data?.optionalFeatures?.qsaCompleteReport;
  const optionalScores = data?.optionalFeatures?.scores?.scoreResponse;
  const paymentHistory =
    data?.optionalFeatures?.advancedCommercialPaymentHistory?.paymentHistory;
  const relationshipSuppliers =
    data?.optionalFeatures?.advancedCommercialPaymentHistory
      ?.relationshipSuppliersPeriods;
  const evolutionCommitments =
    data?.optionalFeatures?.advancedCommercialPaymentHistory
      ?.evolutionCommitmentsSuppliers;

  const renderFaturamentoPresumido = () => {
    const scoreParams =
      data?.optionalFeatures?.scores?.scoreResponse?.[0]?.scoreParam || [];

    const valorEmMilhares = scoreParams.find(
      (item) => item.key === "valorEmMilhares",
    )?.value;

    if (!valorEmMilhares || isNaN(valorEmMilhares)) return null;

    const valorFinal = parseInt(valorEmMilhares, 10) * 1000;

    return (
      <div>
        <TitleStripe className="mb-4 flex items-center gap-2">
          Faturamento Presumido
        </TitleStripe>

        {valorFinal ? (
          <div className="flex flex-row gap-4 items-center bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition">
            <div className="flex items-center gap-4">
              <div className="bg-green-100 text-green-700 p-3 rounded-full">
                <FaChartLine className="text-2xl" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-800 w-[250px]">
                  {formatToReal(valorFinal)}
                </div>

                <div className="text-xs text-gray-400">Estimado ao ano</div>
              </div>
            </div>

            <div className="text-sm text-gray-600 mt-2 ml-6 flex-1">
              <div className="font-medium text-gray-700 mb-1">
                Metodologia aplicada:
              </div>
              <p className="text-justify text-xs leading-relaxed">
                O resultado é calculado por meio de técnicas estatísticas que
                utilizam informações cadastrais e comportamentais da empresa.
              </p>
            </div>
          </div>
        ) : (
          <Alert color="gray">
            Sem informações suficientes para informar o faturamento presumido da
            empresa.
          </Alert>
        )}
      </div>
    );
  };

  const renderLimiteCreditoPJ = () => {
    const score = data?.reports[0]?.scores?.scoreResponse?.[0];

    return (
      <div>
        <TitleStripe className="mb-4 gap-2">Limite de Crédito PJ</TitleStripe>
        {score ? (
          <div className="flex flex-row gap-4 items-center bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition">
            <div className="flex items-center gap-4">
              <div className="bg-green-100 text-green-600 p-3 rounded-full">
                <FaRegCreditCard className="text-2xl" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-700 w-[250px]">
                  {formatToReal(score.score)}
                </div>
                <div className="text-xs text-gray-400">
                  Score de Limite de Crédito
                </div>
              </div>
            </div>

            <div className="text-sm text-gray-600 mt-2 ml-6 flex-1">
              <div className="font-medium text-gray-700 mb-1">
                Critérios utilizados:
              </div>
              <p className="text-justify text-xs leading-relaxed">
                {score.message}
              </p>
            </div>
          </div>
        ) : (
          <Alert color="gray">
            Sem informações suficientes para informar o limite de crédito
            sugerido.
          </Alert>
        )}
      </div>
    );
  };

  const renderRelacionamentoComFornecedores = () => (
    <>
      {relationshipSuppliers && (
        <div className="mt-1">
          <TitleStripe className="mb-5 flex items-center gap-2">
            Relacionamento com Fornecedores
          </TitleStripe>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {relationshipSuppliers?.relationshipSuppliersPeriodList?.map(
              (item, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl shadow-sm bg-white border border-gray-100 transition p-4 flex flex-col justify-between"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-600">
                      {item.relationshipPeriodDescription.replace(":", "")}
                    </div>
                    <FaHandshake className="text-green-600 text-xl" />
                  </div>

                  <div className="mt-2">
                    <div className="text-[13px] text-gray-500 mb-1">
                      Quantidade de fontes:
                    </div>
                    <div className="text-3xl font-bold text-green-700 leading-tight">
                      {item.relationshipSourceQuantity}
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      )}
    </>
  );

  const renderPrincipaisFornecedores = () => {
    const fornecedores =
      data?.optionalFeatures?.advancedCommercialPaymentHistory?.mainSuppliers
        ?.mainSuppliersList;

    return fornecedores?.length > 0 ? (
      <div className="mt-4">
        <TitleStripe className="mb-5 flex items-center gap-2">
          Principais Fornecedores
        </TitleStripe>
        <div className="overflow-x-auto border border-gray-100 rounded-lg shadow-sm">
          <Table striped>
            <Table.Head>
              <Table.HeadCell className="text-black bg-gray-100">
                Razão Social
              </Table.HeadCell>
              <Table.HeadCell className="text-center text-black bg-gray-100">
                CNPJ do Fornecedor
              </Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {fornecedores.map((item, idx) => (
                <Table.Row key={idx} className="text-black">
                  <Table.Cell className="text-sm py-2 px-3">
                    {item.supplierName}
                  </Table.Cell>
                  <Table.Cell className="text-sm py-2 px-3 text-center">
                    {item.supplierDocument}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
        <span className="mt-2 block text-xs italic">
          Exibindo as últimas 5 informações
        </span>
      </div>
    ) : null;
  };

  const renderEvolucaoCompromissos = () => {
    const rawData =
      evolutionCommitments?.evolutionCommitmentsSuppliersList || [];

    const reversedData = [...rawData].reverse(); // 👈 inverte o array original

    const categories = reversedData.map(
      (item) => `${item.descriptionMonthCommitment}/${item.yearCommitment}`,
    );

    const values = reversedData.map((item) =>
      parseFloat(item.totalMonthlyRangeValueFrom || 0),
    );

    const descriptions = reversedData.map(
      (item) => item.totalMonthRangeDescription || "",
    );

    const options = {
      chart: {
        type: "bar",
        height: 350,
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: false,
          columnWidth: "50%",
          dataLabels: {
            position: "top", // coloca acima
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: function (_, opts) {
          const desc = descriptions[opts.dataPointIndex] || "";
          return desc.length > 20 ? desc.slice(0, 20) + "…" : desc;
        },
        style: {
          fontSize: "10px",
          colors: ["#333"],
        },
        offsetY: -20,
      },
      xaxis: {
        categories,
        labels: {
          rotate: -45,
          style: {
            fontSize: "11px",
          },
        },
      },
      yaxis: {
        labels: {
          formatter: (val) => `R$ ${val.toLocaleString("pt-BR")}`,
        },
      },
      tooltip: {
        y: {
          formatter: (val) =>
            `R$ ${val.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`,
        },
      },
      colors: ["#16a34a"],
    };

    const series = [
      {
        name: "Compromissos",
        data: values,
      },
    ];

    return (
      <div className="mt-6">
        <TitleStripe className="mb-4">
          Evolução de Compromissos com Fornecedores
        </TitleStripe>
        {rawData?.length > 0 ? (
          <ReactApexChart
            options={options}
            series={series}
            type="bar"
            height={320}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>
    );
  };

  const renderHistoricoPagamentos = () => {
    if (!paymentHistory || !paymentHistory.titlesQuantity) return null;
    const formatPercentage = (from, to) =>
      `${(from / 100).toFixed(1)}% a ${(to / 100).toFixed(1)}%`;
    const formatCurrencyRange = (from, to) => {
      const formatValue = (value) => {
        if (value >= 1_000_000) {
          return `R$ ${(value / 1_000_000).toFixed(1)} MI`;
        } else if (value >= 1_000) {
          return `R$ ${(value / 1_000).toFixed(1)} mil`;
        }
        return `R$ ${value.toFixed(2)}`;
      };

      return `${formatValue(from)} a ${formatValue(to)}`;
    };

    const titles = paymentHistory.titlesQuantity;
    const months = paymentHistory.monthDetail.months;
    const periodList = paymentHistory.averageDelayPeriod.periodList;

    const allRanges = Array.from(
      new Set(months.flatMap((m) => m.periodList.map((p) => p.name))),
    );

    const getPeriodData = (month, rangeName) => {
      return month.periodList.find((p) => p.name === rangeName);
    };

    return (
      <div>
        <div>
          <TitleStripe className="mb-5">Histórico de Pagamentos</TitleStripe>

          <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
            {titles.map((item, idx) => (
              <div
                key={idx}
                className="p-3 border rounded-md bg-white shadow-sm text-center hover:bg-green-50"
              >
                <div className="text-sm font-medium text-gray-600 mb-1">
                  {item.name}
                </div>
                <div className="text-xs text-gray-500">Faixa: {item.range}</div>
                <div className="text-green-700 font-bold mt-1">
                  {item.percentage}
                </div>
              </div>
            ))}
          </div>
        </div>
        {allRanges?.length > 0 && (
          <>
            {" "}
            <TitleStripe>Comportamento Mensal</TitleStripe>
            <Card className="mt-4">
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs text-gray-700">
                  <thead>
                    <tr className="bg-gray-100 text-[11px] uppercase text-gray-600">
                      <th className="text-left p-2 whitespace-nowrap">
                        Período
                      </th>
                      {allRanges.map((range, i) => (
                        <th
                          key={i}
                          className="text-center p-2 whitespace-nowrap"
                        >
                          {range}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {months.map((month, i) => (
                      <tr
                        key={i}
                        className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="p-2 font-medium text-gray-800 whitespace-nowrap">
                          {month.month}
                        </td>
                        {allRanges.map((range, j) => {
                          const data = getPeriodData(month, range);
                          return (
                            <td
                              key={j}
                              className="p-2 text-center whitespace-nowrap"
                            >
                              {data &&
                              data.percentageFrom != 0 &&
                              data.percentageTo != 0 ? (
                                <>
                                  <div className="text-[14px] text-gray-800">
                                    {formatCurrencyRange(
                                      data.rangeValueFrom,
                                      data.rangeValueTo,
                                    )}
                                  </div>
                                  <div className="text-[14px] text-gray-500 italic">
                                    {formatPercentage(
                                      data.percentageFrom,
                                      data.percentageTo,
                                    )}
                                  </div>
                                </>
                              ) : (
                                <div className="text-[14px] text-gray-800">
                                  -
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
        {periodList?.length > 0 && (
          <>
            <TitleStripe>Atraso Médio por Mês</TitleStripe>
            <Card className="mt-4">
              <div className="grid grid-cols-4 gap-4 px-4 pb-4">
                {periodList.map((p, i) => (
                  <div
                    key={i}
                    className="bg-gray-50 rounded-lg shadow-sm p-3 border border-gray-200 text-center"
                  >
                    <div className="text-[11px] text-gray-500 uppercase font-medium mb-1">
                      {p.period}
                    </div>
                    <div className="text-sm font-semibold text-gray-800">
                      {p.averageDelayDaysFrom} a {p.averageDelayDaysTo} dias
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>
    );
  };

  const renderBusinessReference = () => {
    const businessRef =
      data?.optionalFeatures?.advancedCommercialPaymentHistory
        ?.businessReferences?.businessReferencesList;

    return businessRef?.length > 0 ? (
      <div>
        <TitleStripe className="mb-5">Referências Comerciais</TitleStripe>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {businessRef.map((item, idx) => (
            <div
              key={idx}
              className="p-4 border rounded-md bg-white shadow-sm hover:bg-greem-50 transition"
            >
              <div className="font-semibold text-gray-700 text-sm mb-1">
                {item.businessDescription}
              </div>
              <div className="text-sm text-gray-600 mb-1">
                {item.monthPotentialDate}/{item.yearPotentialDate}
              </div>
              <div className="text-green-700 font-bold">
                Faixa: {item.potentialValueRangeDescription}
              </div>
            </div>
          ))}
        </div>
      </div>
    ) : null;
  };

  return (
    <>
      {data?.statusRetorno == 1 ? (
        <>
          {data?.reports[0]?.identificationReport && (
            <div className="mt-6 border rounded-2xl shadow p-6 bg-white">
              <h3 className="text-lg font-bold mb-4 border-b pb-2">
                📋 Dados Cadastrais
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
                <div>
                  <strong>Razão Social:</strong>{" "}
                  {data.reports[0]?.identificationReport?.companyName ||
                    "Indisponível"}
                </div>
                <div>
                  <strong>Nome Fantasia:</strong>{" "}
                  {data.reports[0]?.identificationReport?.companyAlias ||
                    "Indisponível"}
                </div>
                <div>
                  <strong>CNPJ:</strong>{" "}
                  {data.reports[0]?.identificationReport?.documentNumber ||
                    "Indisponível"}
                </div>
                <div>
                  <strong>Situação:</strong>{" "}
                  {data.reports[0]?.identificationReport
                    ?.statusCodeDescription || "Indisponível"}
                </div>
                <div>
                  <strong>CNAE:</strong>{" "}
                  {data.reports[0]?.identificationReport?.cnae ||
                    "Indisponível"}
                </div>
                <div>
                  <strong>Descrição da Atividade Econômica:</strong>{" "}
                  {data.reports[0]?.identificationReport?.economicActivity ||
                    "Indisponível"}
                </div>
                <div>
                  <strong>NIRE:</strong>{" "}
                  {data.reports[0]?.identificationReport?.nireNumber ||
                    "Indisponível"}
                </div>
                <div>
                  <strong>Natureza Jurídica:</strong>{" "}
                  {data.reports[0]?.identificationReport?.partnership ||
                    "Indisponível"}
                </div>
                <div>
                  <strong>Data de Fundação:</strong>{" "}
                  {data.reports[0]?.identificationReport?.companyFoundation
                    ? moment(
                        data.reports[0]?.identificationReport.companyFoundation,
                      ).format("DD/MM/YYYY")
                    : "Indisponível"}
                </div>
                <div>
                  <strong>Data de Registro:</strong>{" "}
                  {data.reports[0]?.identificationReport?.companyRegisterDate
                    ? moment(
                        data.reports[0]?.identificationReport
                          .companyRegisterDate,
                      ).format("DD/MM/YYYY")
                    : "Indisponível"}
                </div>
                <div>
                  <strong>Número de Funcionários:</strong>{" "}
                  {data.reports[0]?.identificationReport?.numberEmployees ??
                    "Indisponível"}
                </div>
                <div>
                  <strong>Opção Tributária:</strong>{" "}
                  {data.reports[0]?.identificationReport?.taxOption ||
                    "Indisponível"}
                </div>
                <div>
                  <strong>Inscrição Estadual:</strong>{" "}
                  {data.reports[0]?.identificationReport?.stateRegistration ||
                    "Indisponível"}
                </div>
                <div>
                  <strong>Código Natureza Jurídica:</strong>{" "}
                  {data.reports[0]?.identificationReport?.legalNatureCode ||
                    "Indisponível"}
                </div>
                <div>
                  <strong>Compras de Importação:</strong>{" "}
                  {data.reports[0]?.identificationReport?.importPurchases ??
                    "Indisponível"}
                </div>
                <div>
                  <strong>Vendas de Exportação:</strong>{" "}
                  {data.reports[0]?.identificationReport?.exportSales ??
                    "Indisponível"}
                </div>
                <div>
                  <strong>Endereço:</strong>{" "}
                  {data.reports[0]?.identificationReport?.address
                    ?.addressLine || "Indisponível"}
                </div>
                <div>
                  <strong>Bairro:</strong>{" "}
                  {data.reports[0]?.identificationReport?.address?.district ||
                    "Indisponível"}
                </div>
                <div>
                  <strong>Cidade:</strong>{" "}
                  {data.reports[0]?.identificationReport?.address?.city ||
                    "Indisponível"}
                </div>
                <div>
                  <strong>Estado:</strong>{" "}
                  {data.reports[0]?.identificationReport?.address?.state ||
                    "Indisponível"}
                </div>
                <div>
                  <strong>CEP:</strong>{" "}
                  {data.reports[0]?.identificationReport?.address?.zipCode ||
                    "Indisponível"}
                </div>
                <div>
                  <strong>Telefone:</strong>{" "}
                  {data.reports[0]?.identificationReport?.phone?.phoneNumber
                    ? `(${data.reports[0]?.identificationReport.phone.areaCode}) ${data.reports[0]?.identificationReport.phone.phoneNumber}`
                    : "Indisponível"}
                </div>
                <div>
                  <strong>Site:</strong>{" "}
                  {data.reports[0]?.identificationReport?.companyUrl ||
                    "Indisponível"}
                </div>
              </div>
            </div>
          )}

          {renderFaturamentoPresumido()}
          {renderLimiteCreditoPJ()}
          {data?.reports[0]?.QSAReport && (
            <div className="px-3 pb-3 rounded-md">
              <TitleStripe className="mb-4">Informações da empresa</TitleStripe>
              <div className="grid grid-cols-2 gap-4 text-sm border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                <div>
                  <span className="font-semibold">Capital Social:</span>
                  <br />
                  <span className="font-semibold text-xl text-green-700">
                    {formatToReal(
                      data?.reports[0]?.QSAReport?.companyData
                        ?.socialCapitalValue,
                    ) || "Indisponível"}
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Capital Realizado:</span>
                  <br />
                  <span className="font-semibold text-xl text-green-700">
                    {formatToReal(
                      data?.reports[0]?.QSAReport?.companyData
                        ?.accomplishedValue,
                    ) || "Indisponível"}
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Atualizado em:</span>
                  <br />
                  {data?.reports[0]?.QSAReport?.companyData
                    ?.accomplishedValueinformationUpdateDate
                    ? moment(
                        data?.reports[0]?.QSAReport?.companyData
                          ?.accomplishedValueinformationUpdateDate,
                      ).format("DD/MM/YYYY")
                    : "Indisponível"}
                </div>
                <div>
                  <span className="font-semibold">País de Origem:</span>
                  <br />
                  {data?.reports[0]?.QSAReport?.companyData
                    ?.accomplishedValuecountryOrigin || "Indisponível"}
                </div>
                <div>
                  <span className="font-semibold">Tipo de Controle:</span>
                  <br />
                  <span className="font-semibold text-xl text-green-700">
                    {data?.reports[0]?.QSAReport?.companyData
                      ?.accomplishedValuecontrolType || "Indisponível"}
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Natureza Jurídica:</span>
                  <br />
                  <span className="font-semibold text-xl text-green-700">
                    {data?.reports[0]?.QSAReport?.companyData
                      ?.accomplishedValuenature || "Indisponível"}
                  </span>
                </div>
              </div>

              <div>
                <TitleStripe className="mb-5">
                  Participação Societária
                </TitleStripe>

                {data?.reports[0]?.QSAReport?.partnerCompleteReport
                  ?.partnersList?.length > 0 ? (
                  <div className="overflow-x-hidden border-gray-100 border rounded-lg mt-3">
                    <Table striped>
                      <Table.Head className="text-black">
                        <Table.HeadCell
                          className="text-center py-2 px-1"
                          style={{ background: "#3EED98" }}
                        >
                          Nº
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="py-2"
                          style={{ background: "#3EED98" }}
                        >
                          Nome
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center py-2 px-1"
                          style={{ background: "#3EED98" }}
                        >
                          Part. (%)
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center py-2 px-1"
                          style={{ background: "#3EED98" }}
                        >
                          Débitos
                          <br />
                          (S/N)
                        </Table.HeadCell>
                      </Table.Head>
                      {data?.reports[0]?.QSAReport?.partnerCompleteReport
                        ?.partnersList?.length > 0 && (
                        <Table.Body className="divide-y">
                          {data?.reports[0]?.QSAReport?.partnerCompleteReport?.partnersList?.map(
                            (socio, index) => (
                              <Table.Row
                                key={index}
                                className="bg-white dark:border-gray-700 dark:bg-gray-800 text-black"
                              >
                                <Table.Cell className="font-medium dark:text-white text-center py-1 px-1">
                                  {index + 1}
                                </Table.Cell>
                                <Table.Cell className=" py-1">
                                  <div>{socio?.name || "-"}</div>
                                  <div style={{ fontSize: "10px" }}>
                                    CPF/CNPJ: {socio?.documentId}
                                  </div>
                                </Table.Cell>
                                <Table.Cell className="text-center py-1 px-1 px-1">
                                  {socio?.capitalTotalValue || "-"}
                                </Table.Cell>
                                <Table.Cell className="text-center py-1 px-1">
                                  {socio?.restrictionSign ? "SIM" : "NÃO"}
                                </Table.Cell>
                              </Table.Row>
                            ),
                          )}
                        </Table.Body>
                      )}
                    </Table>
                  </div>
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">
                  Administradores/Diretores
                </TitleStripe>
                {data?.reports[0]?.QSAReport?.partnerCompleteReport
                  ?.partnersListdirectorCompleteReport?.directorsList?.length >
                0 ? (
                  <div className="overflow-x-hidden border-gray-100 border rounded-lg mt-3">
                    <Table striped>
                      <Table.Head className="text-black">
                        <Table.HeadCell
                          className="text-center py-2 px-1"
                          style={{ background: "#3EED98" }}
                        >
                          Nº
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="py-2"
                          style={{ background: "#3EED98" }}
                        >
                          Nome
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center py-2 px-1"
                          style={{ background: "#3EED98" }}
                        >
                          Cargo
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center py-2 px-1"
                          style={{ background: "#3EED98" }}
                        >
                          Desde
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center py-2 px-1"
                          style={{ background: "#3EED98" }}
                        >
                          Débitos (S/N)
                        </Table.HeadCell>
                      </Table.Head>
                      <Table.Body className="divide-y">
                        {data?.reports[0]?.QSAReport?.partnerCompleteReport?.partnersListdirectorCompleteReport?.directorsList.map(
                          (diretor, index) => (
                            <Table.Row
                              key={index}
                              className="bg-white dark:border-gray-700 dark:bg-gray-800 text-black"
                            >
                              <Table.Cell className="font-medium dark:text-white text-center py-1 px-1">
                                {index + 1}
                              </Table.Cell>
                              <Table.Cell className="py-1">
                                <div>{diretor?.name || "-"}</div>
                                <div style={{ fontSize: "10px" }}>
                                  CPF/CNPJ: {diretor?.documentId}
                                </div>
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 px-1">
                                {diretor?.role || "-"}
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 px-1">
                                {diretor?.sinceDate
                                  ? moment(diretor.sinceDate).format(
                                      "DD/MM/yyyy",
                                    )
                                  : "-"}
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 px-1">
                                {diretor?.restrictionSign ? "SIM" : "NÃO"}
                              </Table.Cell>
                            </Table.Row>
                          ),
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                ) : (
                  <NoOcurrence />
                )}
              </div>
            </div>
          )}

          {optionalQsa && (
            <>
              <div>
                <TitleStripe className="mb-5">
                  Informações da empresa
                </TitleStripe>
                {optionalQsa ? (
                  <div className="grid grid-cols-2 gap-4 text-sm border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                    <div>
                      <span className="font-semibold">Capital Social:</span>
                      <br />
                      <span className="font-semibold text-xl text-green-700">
                        {formatToReal(optionalQsa.shareCapital?.capitalValue) ||
                          "Indisponível"}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold">Capital Realizado:</span>
                      <br />
                      <span className="font-semibold text-xl text-green-700">
                        {formatToReal(
                          optionalQsa.shareCapital?.realizedCapitalValue,
                        ) || "Indisponível"}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold">Atualizado em:</span>
                      <br />
                      {optionalQsa.shareCapital?.updateDate
                        ? moment(optionalQsa.shareCapital?.updateDate).format(
                            "DD/MM/YYYY",
                          )
                        : "Indisponível"}
                    </div>
                    <div>
                      <span className="font-semibold">Origem:</span>
                      <br />
                      {optionalQsa.shareCapital?.origin || "Indisponível"}
                    </div>
                    <div>
                      <span className="font-semibold">Controle:</span>
                      <br />
                      {optionalQsa.shareCapital?.control || "Indisponível"}
                    </div>
                    <div>
                      <span className="font-semibold">Natureza:</span>
                      <br />
                      {optionalQsa.shareCapital?.nature || "Indisponível"}
                    </div>
                  </div>
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <>
                <div>
                  <TitleStripe className="mb-1">
                    Dados Detalhados dos Sócios
                  </TitleStripe>
                  {optionalQsa?.partners?.length > 0 ? (
                    <div className="flex flex-col gap-4 mt-4">
                      {optionalQsa.partners.map((partner, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-md p-4 text-sm bg-white shadow-sm"
                        >
                          <div className="font-semibold text-base mb-2">
                            {partner.name}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <strong>Documento:</strong> {partner.document}-
                              {partner.documentDigit}
                            </div>
                            <div>
                              <strong>Participação:</strong>{" "}
                              {partner.percentageCapital}%
                            </div>
                            <div>
                              <strong>Data de Entrada:</strong>{" "}
                              {moment(partner.entryDate).format("DD/MM/YYYY")}
                            </div>
                            <div>
                              <strong>Nascimento:</strong>{" "}
                              {moment(partner.birthDate).format("DD/MM/YYYY")}
                            </div>
                            <div>
                              <strong>Endereço:</strong>{" "}
                              {partner.address?.addressLine},{" "}
                              {partner.address?.district},{" "}
                              {partner.address?.city} - {partner.address?.state}
                              , CEP {partner.address?.zipCode}
                            </div>
                            <div>
                              <strong>Telefone:</strong> (
                              {partner.phone?.areaCode}){" "}
                              {partner.phone?.phoneNumber}
                            </div>
                          </div>

                          <div className="mt-4">
                            <strong className="block mb-1">Débitos:</strong>
                            {partner.debts?.some(
                              (debt) => debt.summary.count > 0,
                            ) ? (
                              <div className="grid grid-cols-2 gap-2">
                                {partner.debts
                                  .filter((debt) => debt.summary.count > 0)
                                  .map((debt, i) => (
                                    <div
                                      key={i}
                                      className="p-3 rounded-md border text-sm bg-red-100 border-red-400"
                                    >
                                      <div className="font-semibold mb-1">
                                        {debtTypeLabels[debt.debtType] ||
                                          debt.debtType}
                                      </div>
                                      <div>
                                        <strong>Ocorrências:</strong>{" "}
                                        {debt.summary.count}
                                      </div>
                                      <div>
                                        <strong>Total:</strong>{" "}
                                        {formatToReal(debt.summary.balance)}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            ) : (
                              <div className="p-3 rounded-md border text-sm bg-green-50 border-green-300 text-center text-green-800">
                                Nenhum débito registrado para este sócio.
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <NoOcurrence />
                  )}
                </div>
                <div>
                  <TitleStripe className="mt-10 mb-1">
                    Dados Detalhados dos Administradores
                  </TitleStripe>
                  {optionalQsa?.administrators?.length > 0 ? (
                    <div className="flex flex-col gap-4 mt-4">
                      {optionalQsa.administrators.map((admin, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-md p-4 text-sm bg-white shadow-sm"
                        >
                          <div className="font-semibold text-base mb-2">
                            {admin.name} ({admin.office})
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <strong>Documento:</strong> {admin.document}-
                              {admin.documentDigit}
                            </div>
                            <div>
                              <strong>Início do Mandato:</strong>{" "}
                              {moment(admin.startDateTerm).format("DD/MM/YYYY")}
                            </div>
                            <div>
                              <strong>Nascimento:</strong>{" "}
                              {moment(admin.birthDate).format("DD/MM/YYYY")}
                            </div>
                            <div>
                              <strong>Telefone:</strong> (
                              {admin.phone?.areaCode}){" "}
                              {admin.phone?.phoneNumber}
                            </div>
                            <div>
                              <strong>Endereço:</strong>{" "}
                              {admin.address?.addressLine},{" "}
                              {admin.address?.district}, {admin.address?.city} -{" "}
                              {admin.address?.state}, CEP{" "}
                              {admin.address?.zipCode}
                            </div>
                          </div>

                          <div className="mt-4">
                            <strong className="block mb-1">Débitos:</strong>
                            {admin.debts?.some(
                              (debt) => debt.summary.count > 0,
                            ) ? (
                              <div className="grid grid-cols-2 gap-2">
                                {admin.debts
                                  .filter((debt) => debt.summary.count > 0)
                                  .map((debt, i) => (
                                    <div
                                      key={i}
                                      className="p-3 rounded-md border text-sm bg-red-100 border-red-400"
                                    >
                                      <div className="font-semibold mb-1">
                                        {debtTypeLabels[debt.debtType] ||
                                          debt.debtType}
                                      </div>
                                      <div>
                                        <strong>Ocorrências:</strong>{" "}
                                        {debt.summary.count}
                                      </div>
                                      <div>
                                        <strong>Total:</strong>{" "}
                                        {formatToReal(debt.summary.balance)}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            ) : (
                              <div className="p-3 rounded-md border text-sm bg-green-50 border-green-300 text-center text-green-800">
                                Nenhum débito registrado para este
                                administrador.
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <NoOcurrence />
                  )}
                </div>
              </>
            </>
          )}

          <div className="-mb-10">
            <div>
              <TitleStripe>Score de risco</TitleStripe>
            </div>
            <div className="flex flex-col gap-2 items-center">
              {data?.reports[0]?.score?.score ? (
                <div className="grid max-w-md items-center gap-4 mb-3 mt-2">
                  <div className="p-4">
                    <img
                      src={getImageURLScore(+data?.reports[0]?.score?.score)}
                    />
                    <div className="text-center font-semibold text-3xl bg-gray-100 w-fit mx-auto py-1 px-4 rounded-md">
                      {data?.reports[0]?.score?.score}
                    </div>
                  </div>
                  <div className="text-center text-sm text-gray-600 mb-4 -mt-4 bg-gray-50 p-2 border border-gray-300">
                    {getScoreDescricao(data?.reports[0]?.score?.score)}
                  </div>
                </div>
              ) : (
                <Alert className="mt-2 mb-4 w-full" color="gray">
                  {data?.reports[0]?.score?.message}
                </Alert>
              )}
            </div>
          </div>

          {/* <EvaluationScore
            data={data}
            score={data?.consultaCredito?.score?.score}
            probability={data?.consultaCredito?.score?.probabilidade}
          /> */}

          {renderHistoricoPagamentos()}
          {renderEvolucaoCompromissos()}
          {renderRelacionamentoComFornecedores()}
          {renderPrincipaisFornecedores()}
          {renderBusinessReference()}

          <div>
            <TitleStripe className="mb-5">Histórico de consultas</TitleStripe>
            {data?.reports[0]?.facts?.inquiryCompanyResponse?.results?.length >
            0 ? (
              <>
                {data?.reports[0]?.facts?.inquiryCompanyResponse?.quantity
                  ?.actual && (
                  <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-sm font-medium">
                    <span>Quantidade Total Mês:</span>
                    <span>
                      {
                        data?.reports[0]?.facts?.inquiryCompanyResponse
                          ?.quantity?.actual
                      }
                    </span>
                  </div>
                )}
                <div className="overflow-x-hidden border-gray-100 border rounded-lg mt-3">
                  <Table striped>
                    <Table.Head>
                      <Table.HeadCell
                        className="text-center text-black"
                        style={{ background: "#3EED98" }}
                      >
                        Data
                      </Table.HeadCell>
                      <Table.HeadCell
                        className="text-center text-black"
                        style={{ background: "#3EED98" }}
                      >
                        Empresa
                      </Table.HeadCell>
                    </Table.Head>
                    {data?.reports[0]?.facts?.inquiryCompanyResponse?.results
                      ?.length > 0 && (
                      <Table.Body className="divide-y">
                        {data?.reports[0]?.facts?.inquiryCompanyResponse?.results?.map(
                          (pendencia, index) => (
                            <Table.Row
                              key={index}
                              className="bg-white dark:border-gray-700 dark:bg-gray-800"
                            >
                              <Table.Cell className="font-medium text-black dark:text-white text-center py-1">
                                {moment(pendencia?.occurrenceDate).format(
                                  "DD/MM/yyyy",
                                ) || "-"}
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 text-black">
                                {(pendencia?.companyName
                                  ?.toUpperCase()
                                  .includes("ENJOY") ||
                                pendencia?.companyName
                                  ?.toUpperCase()
                                  .includes("42.283.377") ||
                                pendencia?.companyName
                                  ?.toUpperCase()
                                  .includes("MEJER")
                                  ? "GRANDES BANCOS"
                                  : pendencia?.companyName) || "-"}
                              </Table.Cell>
                            </Table.Row>
                          ),
                        )}
                      </Table.Body>
                    )}
                  </Table>
                </div>
              </>
            ) : (
              <NoOcurrence />
            )}
            <GraficoConsultasHistorico
              historical={
                data?.reports[0]?.facts?.inquiryCompanyResponse?.quantity
                  ?.historical || []
              }
            />
          </div>

          <div className="-mt-6">
            <TitleStripe className="mb-5">Pendências Comerciais</TitleStripe>
            {data?.reports[0]?.negativeData?.pefin?.pefinResponse?.length >
            0 ? (
              <>
                <div className="flex gap-6">
                  <div className="flex gap-2 items-center">
                    <span className="font-semibold text-red-700">
                      Quantidade:{" "}
                    </span>
                    <Badge color="failure" className="w-fit">
                      {data?.reports[0]?.negativeData?.pefin?.summary?.count}
                    </Badge>
                  </div>

                  {data?.reports[0]?.negativeData?.pefin?.summary
                    ?.firstOccurrence && (
                    <div className="flex gap-2 items-center">
                      <span className="font-semibold text-red-700">
                        Dt. primeira:{" "}
                      </span>
                      <Badge color="failure" className="w-fit">
                        {moment(
                          data?.reports[0]?.negativeData?.pefin?.summary
                            ?.firstOccurrence,
                        ).format("DD/MM/yyyy")}
                      </Badge>
                    </div>
                  )}

                  {data?.reports[0]?.negativeData?.pefin?.summary
                    ?.lastOccurrence && (
                    <div className="flex gap-2 items-center">
                      <span className="font-semibold text-red-700">
                        Dt. última:{" "}
                      </span>
                      <Badge color="failure" className="w-fit">
                        {moment(
                          data?.reports[0]?.negativeData?.pefin?.summary
                            ?.lastOccurrence,
                        ).format("DD/MM/yyyy")}
                      </Badge>
                    </div>
                  )}

                  <div className="flex gap-2 items-center">
                    <span className="font-semibold text-red-700">Total: </span>
                    <Badge color="failure" className="w-fit">
                      {formatToReal(
                        data?.reports[0]?.negativeData?.pefin?.summary?.balance,
                      )}
                    </Badge>
                  </div>
                </div>
                <div className="overflow-x-hidden border-gray-100 border rounded-lg mt-3">
                  <Table striped>
                    <Table.Head>
                      <Table.HeadCell
                        className="text-center text-black"
                        style={{ background: "#3EED98" }}
                      >
                        Data
                      </Table.HeadCell>
                      <Table.HeadCell
                        className="text-center text-black"
                        style={{ background: "#3EED98" }}
                      >
                        Informante
                      </Table.HeadCell>
                      <Table.HeadCell
                        className="text-center text-black"
                        style={{ background: "#3EED98" }}
                      >
                        Valor
                      </Table.HeadCell>
                      <Table.HeadCell
                        className="text-center text-black"
                        style={{ background: "#3EED98" }}
                      >
                        Contrato
                      </Table.HeadCell>
                    </Table.Head>
                    {data?.reports[0]?.negativeData?.pefin?.pefinResponse
                      ?.length > 0 && (
                      <Table.Body className="divide-y">
                        {data?.reports[0]?.negativeData?.pefin?.pefinResponse
                          ?.slice(0, 5)
                          ?.map((pendencia, index) => (
                            <Table.Row
                              key={index}
                              className="bg-white dark:border-gray-700 dark:bg-gray-800"
                            >
                              <Table.Cell className="font-medium text-black dark:text-white text-center py-1">
                                {moment(pendencia?.occurrenceDate).format(
                                  "DD/MM/yyyy",
                                ) || "-"}
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 text-black">
                                {pendencia?.creditorName || "-"}
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 text-black">
                                {formatToReal(pendencia?.amount)}
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 text-black">
                                {pendencia?.contractId || "-"}
                              </Table.Cell>
                            </Table.Row>
                          ))}
                      </Table.Body>
                    )}
                  </Table>
                </div>
                <span className="mt-2 block text-xs italic">
                  Exibindo as últimas 5 ocorrências
                </span>
              </>
            ) : (
              <NoOcurrence />
            )}

            <div>
              <TitleStripe className="mb-5">Pendências Financeiras</TitleStripe>
              {data?.reports[0]?.negativeData?.refin?.refinResponse?.length >
              0 ? (
                <>
                  <div className="flex gap-6">
                    <div className="flex gap-2 items-center">
                      <span className="font-semibold text-red-700">
                        Quantidade:{" "}
                      </span>
                      <Badge color="failure" className="w-fit">
                        {data?.reports[0]?.negativeData?.refin?.summary?.count}
                      </Badge>
                    </div>

                    {data?.reports[0]?.negativeData?.refin?.summary
                      ?.firstOccurrence && (
                      <div className="flex gap-2 items-center">
                        <span className="font-semibold text-red-700">
                          Dt. primeira:{" "}
                        </span>
                        <Badge color="failure" className="w-fit">
                          {moment(
                            data?.reports[0]?.negativeData?.refin?.summary
                              ?.firstOccurrence,
                          ).format("DD/MM/yyyy")}
                        </Badge>
                      </div>
                    )}

                    {data?.reports[0]?.negativeData?.refin?.summary
                      ?.lastOccurrence && (
                      <div className="flex gap-2 items-center">
                        <span className="font-semibold text-red-700">
                          Dt. última:{" "}
                        </span>
                        <Badge color="failure" className="w-fit">
                          {moment(
                            data?.reports[0]?.negativeData?.refin?.summary
                              ?.lastOccurrence,
                          ).format("DD/MM/yyyy")}
                        </Badge>
                      </div>
                    )}

                    <div className="flex gap-2 items-center">
                      <span className="font-semibold text-red-700">
                        Total:{" "}
                      </span>
                      <Badge color="failure" className="w-fit">
                        {formatToReal(
                          data?.reports[0]?.negativeData?.refin?.summary
                            ?.balance,
                        )}
                      </Badge>
                    </div>
                  </div>
                  <div className="overflow-x-hidden border-gray-100 border rounded-lg mt-3">
                    <Table striped>
                      <Table.Head>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Data
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Informante
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Valor
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Contrato
                        </Table.HeadCell>
                      </Table.Head>
                      {data?.reports[0]?.negativeData?.refin?.refinResponse
                        ?.length > 0 && (
                        <Table.Body className="divide-y">
                          {data?.reports[0]?.negativeData?.refin?.refinResponse
                            ?.slice(0, 5)
                            ?.map((pendencia, index) => (
                              <Table.Row
                                key={index}
                                className="bg-white dark:border-gray-700 dark:bg-gray-800"
                              >
                                <Table.Cell className="font-medium text-black dark:text-white text-center py-1">
                                  {moment(pendencia?.occurrenceDate).format(
                                    "DD/MM/yyyy",
                                  ) || "-"}
                                </Table.Cell>
                                <Table.Cell className="text-center py-1 text-black">
                                  {pendencia?.creditorName || "-"}
                                </Table.Cell>
                                <Table.Cell className="text-center py-1 text-black">
                                  {formatToReal(pendencia?.amount)}
                                </Table.Cell>
                                <Table.Cell className="text-center py-1 text-black">
                                  {pendencia?.contractId || "-"}
                                </Table.Cell>
                              </Table.Row>
                            ))}
                        </Table.Body>
                      )}
                    </Table>
                  </div>
                  <span className="mt-2 block text-xs italic">
                    Exibindo as últimas 5 ocorrências
                  </span>
                </>
              ) : (
                <NoOcurrence />
              )}
            </div>

            <div>
              <TitleStripe className="mb-5">Cheques sem fundos</TitleStripe>
              {data?.reports[0]?.negativeData?.check?.checkResponse?.length >
              0 ? (
                <>
                  <div className="flex gap-6">
                    <div className="flex gap-2 items-center">
                      <span className="font-semibold text-red-700">
                        Quantidade:{" "}
                      </span>
                      <Badge color="failure" className="w-fit">
                        {data?.reports[0]?.negativeData?.check?.summary?.count}
                      </Badge>
                    </div>

                    {data?.reports[0]?.negativeData?.check?.summary
                      ?.firstOccurrence && (
                      <div className="flex gap-2 items-center">
                        <span className="font-semibold text-red-700">
                          Dt. primeira:{" "}
                        </span>
                        <Badge color="failure" className="w-fit">
                          {moment(
                            data?.reports[0]?.negativeData?.check?.summary
                              ?.firstOccurrence,
                          ).format("DD/MM/yyyy")}
                        </Badge>
                      </div>
                    )}

                    {data?.reports[0]?.negativeData?.check?.summary
                      ?.lastOccurrence && (
                      <div className="flex gap-2 items-center">
                        <span className="font-semibold text-red-700">
                          Dt. última:{" "}
                        </span>
                        <Badge color="failure" className="w-fit">
                          {moment(
                            data?.reports[0]?.negativeData?.check?.summary
                              ?.lastOccurrence,
                          ).format("DD/MM/yyyy")}
                        </Badge>
                      </div>
                    )}

                    <div className="flex gap-2 items-center">
                      <span className="font-semibold text-red-700">
                        Total:{" "}
                      </span>
                      <Badge color="failure" className="w-fit">
                        {formatToReal(
                          data?.reports[0]?.negativeData?.check?.summary
                            ?.balance,
                        )}
                      </Badge>
                    </div>
                  </div>
                  <div className="overflow-x-hidden border-gray-100 border rounded-lg mt-3">
                    <Table striped>
                      <Table.Head>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Data
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Banco
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Nº Cheque
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Qtd.
                        </Table.HeadCell>
                      </Table.Head>
                      {data?.reports[0]?.negativeData?.check?.checkResponse
                        ?.length > 0 && (
                        <Table.Body className="divide-y">
                          {data?.reports[0]?.negativeData?.check?.checkResponse?.map(
                            (pendencia, index) => (
                              <Table.Row
                                key={index}
                                className="bg-white dark:border-gray-700 dark:bg-gray-800"
                              >
                                <Table.Cell className="font-medium text-black dark:text-white text-center py-1">
                                  {moment(pendencia?.occurrenceDate).format(
                                    "DD/MM/yyyy",
                                  ) || "-"}
                                </Table.Cell>
                                <Table.Cell className="text-center py-1 text-black">
                                  {pendencia?.bankName || "-"}
                                </Table.Cell>
                                <Table.Cell className="text-center py-1 text-black">
                                  {pendencia?.checkNumber || "-"}
                                </Table.Cell>
                                <Table.Cell className="text-center py-1 text-black">
                                  {pendencia?.checkCount || "-"}
                                </Table.Cell>
                              </Table.Row>
                            ),
                          )}
                        </Table.Body>
                      )}
                    </Table>
                  </div>
                  <span className="mt-2 block text-xs italic">
                    Exibindo as últimas 5 ocorrências
                  </span>
                </>
              ) : (
                <NoOcurrence />
              )}
            </div>
            <div>
              <TitleStripe className="mb-5">Protestos</TitleStripe>
              {data?.reports[0]?.negativeData?.notary?.notaryResponse?.length >
              0 ? (
                <>
                  <div className="flex gap-6">
                    <div className="flex gap-2 items-center">
                      <span className="font-semibold text-red-700">
                        Quantidade:{" "}
                      </span>
                      <Badge color="failure" className="w-fit">
                        {data?.reports[0]?.negativeData?.notary?.summary?.count}
                      </Badge>
                    </div>

                    {data?.reports[0]?.negativeData?.notary?.summary
                      ?.firstOccurrence && (
                      <div className="flex gap-2 items-center">
                        <span className="font-semibold text-red-700">
                          Dt. primeira:{" "}
                        </span>
                        <Badge color="failure" className="w-fit">
                          {moment(
                            data?.reports[0]?.negativeData?.notary?.summary
                              ?.firstOccurrence,
                          ).format("DD/MM/yyyy")}
                        </Badge>
                      </div>
                    )}

                    {data?.reports[0]?.negativeData?.notary?.summary
                      ?.lastOccurrence && (
                      <div className="flex gap-2 items-center">
                        <span className="font-semibold text-red-700">
                          Dt. última:{" "}
                        </span>
                        <Badge color="failure" className="w-fit">
                          {moment(
                            data?.reports[0]?.negativeData?.notary?.summary
                              ?.lastOccurrence,
                          ).format("DD/MM/yyyy")}
                        </Badge>
                      </div>
                    )}

                    <div className="flex gap-2 items-center">
                      <span className="font-semibold text-red-700">
                        Total:{" "}
                      </span>
                      <Badge color="failure" className="w-fit">
                        {formatToReal(
                          data?.reports[0]?.negativeData?.notary?.summary
                            ?.balance,
                        )}
                      </Badge>
                    </div>
                  </div>
                  <div className="overflow-x-hidden border-gray-100 border rounded-lg mt-3">
                    <Table striped>
                      <Table.Head>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Data
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Cartório
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Valor
                        </Table.HeadCell>
                      </Table.Head>
                      {data?.reports[0]?.negativeData?.notary?.notaryResponse
                        ?.length > 0 && (
                        <Table.Body className="divide-y">
                          {data?.reports[0]?.negativeData?.notary?.notaryResponse?.map(
                            (pendencia, index) => (
                              <Table.Row
                                key={index}
                                className="bg-white dark:border-gray-700 dark:bg-gray-800"
                              >
                                <Table.Cell className="font-medium text-black dark:text-white text-center py-1">
                                  {moment(pendencia?.occurrenceDate).format(
                                    "DD/MM/yyyy",
                                  ) || "-"}
                                </Table.Cell>
                                <Table.Cell className="text-center py-1 text-black">
                                  {pendencia?.officeNumber || "-"}
                                </Table.Cell>
                                <Table.Cell className="text-center py-1 text-black">
                                  {formatToReal(pendencia?.amount)}
                                </Table.Cell>
                              </Table.Row>
                            ),
                          )}
                        </Table.Body>
                      )}
                    </Table>
                  </div>
                  <span className="mt-2 block text-xs italic">
                    Exibindo as últimas 5 ocorrências
                  </span>
                </>
              ) : (
                <NoOcurrence />
              )}
            </div>

            <div>
              <TitleStripe className="mb-5">Ações Judiciais</TitleStripe>
              {data?.facts?.judgementFilings?.judgementFilingsResponse?.length >
              0 ? (
                <>
                  <div className="flex gap-6">
                    <div className="flex gap-2 items-center">
                      <span className="font-semibold text-red-700">
                        Quantidade:{" "}
                      </span>
                      <Badge color="failure" className="w-fit">
                        {data?.facts?.judgementFilings?.summary?.count}
                      </Badge>
                    </div>

                    {data?.facts?.judgementFilings?.summary
                      ?.firstOccurrence && (
                      <div className="flex gap-2 items-center">
                        <span className="font-semibold text-red-700">
                          Dt. primeira:{" "}
                        </span>
                        <Badge color="failure" className="w-fit">
                          {moment(
                            data?.facts?.judgementFilings?.summary
                              ?.firstOccurrence,
                          ).format("DD/MM/yyyy")}
                        </Badge>
                      </div>
                    )}

                    {data?.facts?.judgementFilings?.summary?.lastOccurrence && (
                      <div className="flex gap-2 items-center">
                        <span className="font-semibold text-red-700">
                          Dt. última:{" "}
                        </span>
                        <Badge color="failure" className="w-fit">
                          {moment(
                            data?.facts?.judgementFilings?.summary
                              ?.lastOccurrence,
                          ).format("DD/MM/yyyy")}
                        </Badge>
                      </div>
                    )}

                    <div className="flex gap-2 items-center">
                      <span className="font-semibold text-red-700">
                        Valor Total:{" "}
                      </span>
                      <Badge color="failure" className="w-fit">
                        {formatToReal(
                          data?.facts?.judgementFilings?.summary?.balance || 0,
                        )}
                      </Badge>
                    </div>
                  </div>
                  <div className="overflow-x-hidden border-gray-100 border rounded-lg mt-3">
                    <Table striped>
                      <Table.Head>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Data
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Natureza
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Vara
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Cidade
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          UF
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Valor
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Contestação
                        </Table.HeadCell>
                      </Table.Head>
                      <Table.Body className="divide-y">
                        {data?.facts?.judgementFilings?.judgementFilingsResponse?.map(
                          (acao, index) => (
                            <Table.Row
                              key={index}
                              className="bg-white dark:border-gray-700 dark:bg-gray-800"
                            >
                              <Table.Cell className="font-medium text-black dark:text-white text-center py-1">
                                {moment(acao?.occurrenceDate).format(
                                  "DD/MM/yyyy",
                                )}
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 text-black">
                                {acao?.legalNature || "-"}
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 text-black">
                                {acao?.civilCourt || "-"}
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 text-black">
                                {acao?.city || "-"}
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 text-black">
                                {acao?.state || "-"}
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 text-black">
                                {formatToReal(acao?.amount)}
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 text-black">
                                {acao?.dispute?.disputeIndicativeFlag
                                  ? "Sim"
                                  : "Não"}
                              </Table.Cell>
                            </Table.Row>
                          ),
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                  <span className="mt-2 block text-xs italic">
                    Exibindo as últimas 5 ocorrências
                  </span>
                </>
              ) : (
                <NoOcurrence />
              )}
            </div>

            <div>
              <TitleStripe className="mb-5">Dívidas vencidas</TitleStripe>
              {data?.reports[0]?.negativeData?.collectionRecords
                ?.collectionRecordsResponse?.length > 0 ? (
                <>
                  <div className="flex gap-6">
                    <div className="flex gap-2 items-center">
                      <span className="font-semibold text-red-700">
                        Quantidade:{" "}
                      </span>
                      <Badge color="failure" className="w-fit">
                        {
                          data?.reports[0]?.negativeData?.collectionRecords
                            ?.summary?.count
                        }
                      </Badge>
                    </div>

                    {data?.reports[0]?.negativeData?.collectionRecords?.summary
                      ?.firstOccurrence && (
                      <div className="flex gap-2 items-center">
                        <span className="font-semibold text-red-700">
                          Dt. primeira:{" "}
                        </span>
                        <Badge color="failure" className="w-fit">
                          {moment(
                            data?.reports[0]?.negativeData?.collectionRecords
                              ?.summary?.firstOccurrence,
                          ).format("DD/MM/yyyy")}
                        </Badge>
                      </div>
                    )}

                    {data?.reports[0]?.negativeData?.collectionRecords?.summary
                      ?.lastOccurrence && (
                      <div className="flex gap-2 items-center">
                        <span className="font-semibold text-red-700">
                          Dt. última:{" "}
                        </span>
                        <Badge color="failure" className="w-fit">
                          {moment(
                            data?.reports[0]?.negativeData?.collectionRecords
                              ?.summary?.lastOccurrence,
                          ).format("DD/MM/yyyy")}
                        </Badge>
                      </div>
                    )}

                    <div className="flex gap-2 items-center">
                      <span className="font-semibold text-red-700">
                        Total:{" "}
                      </span>
                      <Badge color="failure" className="w-fit">
                        {formatToReal(
                          data?.reports[0]?.negativeData?.collectionRecords
                            ?.summary?.balance || 0,
                        )}
                      </Badge>
                    </div>
                  </div>

                  <div className="overflow-x-hidden border-gray-100 border rounded-lg mt-3">
                    <Table striped>
                      <Table.Head>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Data
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Credor
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Valor
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center text-black"
                          style={{ background: "#3EED98" }}
                        >
                          Contrato
                        </Table.HeadCell>
                      </Table.Head>
                      <Table.Body className="divide-y">
                        {data?.reports[0]?.negativeData?.collectionRecords?.collectionRecordsResponse?.map(
                          (registro, index) => (
                            <Table.Row
                              key={index}
                              className="bg-white dark:border-gray-700 dark:bg-gray-800"
                            >
                              <Table.Cell className="font-medium text-black dark:text-white text-center py-1">
                                {moment(registro?.occurrenceDate).format(
                                  "DD/MM/yyyy",
                                )}
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 text-black">
                                {registro?.creditorName || "-"}
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 text-black">
                                {formatToReal(registro?.amount)}
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 text-black">
                                {registro?.contractId || "-"}
                              </Table.Cell>
                            </Table.Row>
                          ),
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                  <span className="mt-2 block text-xs italic">
                    Exibindo as últimas 5 ocorrências
                  </span>
                </>
              ) : (
                <NoOcurrence />
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <TitleStripe>Avalia Max PJ</TitleStripe>
          <Alert className="mt-2" color="gray" withBorderAccent>
            Consulta indisponível no momento!
          </Alert>
        </>
      )}
    </>
  );
};

export default RelatorioAvancadoPJQuery;
